import { useMemo, useRef, useState } from "react";
import {
  ArrowDownToLine, ArrowUpFromLine, Boxes, CheckCircle2, FileText, FileUp, History,
  PackageCheck, Search, TriangleAlert,
} from "lucide-react";
import type { Product } from "@arena/shared";
import { formatMoney } from "@arena/shared";
import { api, toCents } from "../api";
import { EmptyState, Modal, PageHeader } from "../components/Layout";
import { useApiData } from "../hooks";

type StockProduct = {
  id: string;
  name: string;
  sku: string | null;
  barcode: string | null;
  stockQuantity: number;
  costCents: number;
  priceCents: number;
  active: boolean;
};

type StockResponse = {
  summary: { trackedProducts: number; totalQuantity: number; outOfStock: number; totalCostValueCents: number };
  products: StockProduct[];
};

type StockMovement = {
  id: string;
  productId: string;
  productName: string;
  type: string;
  source: string;
  quantity: number;
  unitCostCents: number | null;
  balanceAfter: number;
  reason: string;
  reference: string | null;
  createdByName: string;
  createdAt: string;
};

type StockDocument = {
  id: string;
  accessKey: string;
  documentNumber: string | null;
  series: string | null;
  issuedAt: string | null;
  supplierName: string | null;
  supplierTaxId: string | null;
  totalCents: number;
  itemCount: number;
  importedByName: string;
  importedAt: string;
};

type XmlItem = {
  index: number;
  code: string;
  barcode: string | null;
  description: string;
  ncm: string | null;
  cfop: string | null;
  unit: string | null;
  quantity: number;
  unitCostCents: number;
  totalCents: number;
  suggestedProductId: string | null;
};

type XmlDocument = {
  accessKey: string;
  documentNumber: string | null;
  series: string | null;
  issuedAt: string | null;
  supplierName: string | null;
  supplierTaxId: string | null;
  totalCents: number;
  alreadyImported: boolean;
  items: XmlItem[];
};

const emptyStock: StockResponse = {
  summary: { trackedProducts: 0, totalQuantity: 0, outOfStock: 0, totalCostValueCents: 0 },
  products: [],
};

const movementLabels: Record<string, string> = {
  ENTRY: "Entrada",
  EXIT: "Saída",
  SALE: "Venda",
  RETURN: "Retorno",
  ADJUSTMENT_IN: "Ajuste de entrada",
  ADJUSTMENT_OUT: "Ajuste de saída",
};

const sourceLabels: Record<string, string> = {
  MANUAL: "Manual",
  XML_NFE: "XML da NF-e",
  ORDER: "Pedido",
  ORDER_CANCELLATION: "Cancelamento",
  PRODUCT: "Cadastro",
};

function quantity(value: number) {
  return new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 3 }).format(value);
}

function dateTime(value: string | null) {
  return value ? new Date(value).toLocaleString("pt-BR") : "—";
}

export function InventoryPage() {
  const [search, setSearch] = useState("");
  const [view, setView] = useState<"products" | "movements" | "documents">("products");
  const [manualType, setManualType] = useState<"ENTRY" | "EXIT" | null>(null);
  const [xmlOpen, setXmlOpen] = useState(false);
  const stock = useApiData<StockResponse>("/api/stock", emptyStock);
  const movements = useApiData<{ movements: StockMovement[] }>("/api/stock/movements", { movements: [] });
  const documents = useApiData<{ documents: StockDocument[] }>("/api/stock/documents", { documents: [] });
  const catalog = useApiData<{ products: Product[] }>("/api/products", { products: [] });
  const filteredProducts = stock.data.products.filter((product) =>
    [product.name, product.sku, product.barcode].some((value) => value?.toLowerCase().includes(search.toLowerCase())),
  );
  const refreshAll = async () => {
    await Promise.all([stock.refresh(), movements.refresh(), documents.refresh(), catalog.refresh()]);
  };

  return <div className="page inventory-page">
    <PageHeader
      eyebrow="BAR E ESTOQUE"
      title="Controle de estoque"
      description="Entradas, saídas, vendas e importações de notas fiscais em um só lugar."
      actions={<div className="inventory-actions">
        <button className="button button-ghost" onClick={() => setManualType("EXIT")}><ArrowUpFromLine /> Registrar saída</button>
        <button className="button button-ghost" onClick={() => setManualType("ENTRY")}><ArrowDownToLine /> Registrar entrada</button>
        <button className="button button-primary" onClick={() => setXmlOpen(true)}><FileUp /> Importar XML</button>
      </div>}
    />

    <div className="metric-grid inventory-metrics">
      <article className="metric-card tone-teal"><span><Boxes /></span><div><small>Produtos controlados</small><strong>{stock.data.summary.trackedProducts}</strong></div></article>
      <article className="metric-card tone-blue"><span><PackageCheck /></span><div><small>Quantidade em estoque</small><strong>{quantity(stock.data.summary.totalQuantity)}</strong></div></article>
      <article className="metric-card tone-orange"><span><TriangleAlert /></span><div><small>Sem estoque</small><strong>{stock.data.summary.outOfStock}</strong></div></article>
      <article className="metric-card tone-green"><span><FileText /></span><div><small>Valor pelo custo</small><strong>{formatMoney(stock.data.summary.totalCostValueCents)}</strong></div></article>
    </div>

    <div className="inventory-tabs">
      <button className={view === "products" ? "active" : ""} onClick={() => setView("products")}><Boxes /> Posição atual</button>
      <button className={view === "movements" ? "active" : ""} onClick={() => setView("movements")}><History /> Movimentações</button>
      <button className={view === "documents" ? "active" : ""} onClick={() => setView("documents")}><FileText /> Notas importadas</button>
    </div>

    {view === "products" && <>
      <div className="table-toolbar"><label className="search-field"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar produto, SKU ou código de barras" /></label><span>{filteredProducts.length} produtos</span></div>
      <section className="panel table-panel">{stock.loading ? <div className="panel-loading"><span className="spinner" /> Carregando estoque...</div> : stock.error ? <div className="form-error">{stock.error}</div> : filteredProducts.length ? <div className="data-table stock-table"><div className="data-row data-head"><span>Produto</span><span>Códigos</span><span>Custo</span><span>Venda</span><span>Saldo</span></div>{filteredProducts.map((product) => <div className="data-row" key={product.id}><span className="product-cell"><i><Boxes /></i><strong>{product.name}</strong>{!product.active && <small>Inativo</small>}</span><span className="stock-codes"><small>SKU {product.sku || "—"}</small><small>EAN {product.barcode || "—"}</small></span><span>{formatMoney(product.costCents)}</span><strong>{formatMoney(product.priceCents)}</strong><span className={`stock-balance ${product.stockQuantity <= 0 ? "empty" : ""}`}>{quantity(product.stockQuantity)} {product.stockQuantity === 1 ? "un." : "un."}</span></div>)}</div> : <EmptyState icon={Boxes} title="Nenhum produto controlado" description="Ative o controle de estoque em um produto ou importe uma nota fiscal." />}</section>
    </>}

    {view === "movements" && <section className="panel table-panel">{movements.loading ? <div className="panel-loading"><span className="spinner" /> Carregando movimentações...</div> : movements.data.movements.length ? <div className="data-table movement-table"><div className="data-row data-head"><span>Data</span><span>Produto</span><span>Movimento</span><span>Quantidade</span><span>Saldo</span><span>Origem</span></div>{movements.data.movements.map((movement) => { const incoming = ["ENTRY", "RETURN", "ADJUSTMENT_IN"].includes(movement.type); return <div className="data-row" key={movement.id}><span><strong>{dateTime(movement.createdAt)}</strong><small>{movement.createdByName}</small></span><span><strong>{movement.productName}</strong><small>{movement.reason}</small></span><span className={`movement-kind ${incoming ? "incoming" : "outgoing"}`}>{incoming ? <ArrowDownToLine /> : <ArrowUpFromLine />}{movementLabels[movement.type] ?? movement.type}</span><strong className={incoming ? "quantity-in" : "quantity-out"}>{incoming ? "+" : "−"}{quantity(movement.quantity)}</strong><span>{quantity(movement.balanceAfter)}</span><span><strong>{sourceLabels[movement.source] ?? movement.source}</strong><small>{movement.reference}</small></span></div>; })}</div> : <EmptyState icon={History} title="Nenhuma movimentação" description="As entradas, saídas e vendas aparecerão aqui automaticamente." />}</section>}

    {view === "documents" && <section className="panel table-panel">{documents.loading ? <div className="panel-loading"><span className="spinner" /> Carregando notas...</div> : documents.data.documents.length ? <div className="data-table document-table"><div className="data-row data-head"><span>Nota fiscal</span><span>Fornecedor</span><span>Emissão</span><span>Itens</span><span>Total</span><span>Importação</span></div>{documents.data.documents.map((document) => <div className="data-row" key={document.id}><span><strong>NF {document.documentNumber || "Sem número"}</strong><small>Série {document.series || "—"}</small></span><span><strong>{document.supplierName || "Fornecedor não informado"}</strong><small>{document.supplierTaxId}</small></span><span>{dateTime(document.issuedAt)}</span><span>{document.itemCount}</span><strong>{formatMoney(document.totalCents)}</strong><span><strong>{dateTime(document.importedAt)}</strong><small>por {document.importedByName}</small></span></div>)}</div> : <EmptyState icon={FileText} title="Nenhuma nota importada" description="Importe o XML de uma NF-e ou NFC-e para dar entrada nos produtos." />}</section>}

    {manualType && <ManualMovementModal type={manualType} products={catalog.data.products} onClose={() => setManualType(null)} onSaved={async () => { setManualType(null); await refreshAll(); }} />}
    {xmlOpen && <XmlImportModal products={catalog.data.products} onClose={() => setXmlOpen(false)} onImported={refreshAll} />}
  </div>;
}

function ManualMovementModal({ type, products, onClose, onSaved }: { type: "ENTRY" | "EXIT"; products: Product[]; onClose: () => void; onSaved: () => Promise<void> }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault(); setSaving(true); setError("");
    const data = new FormData(event.currentTarget);
    try {
      await api("/api/stock/movements", { method: "POST", body: JSON.stringify({
        productId: data.get("productId"), type, quantity: Number(data.get("quantity")),
        unitCostCents: type === "ENTRY" && data.get("unitCost") ? toCents(String(data.get("unitCost"))) : null,
        reason: data.get("reason"), reference: data.get("reference") || null,
      }) });
      await onSaved();
    } catch (requestError) { setError((requestError as Error).message); }
    finally { setSaving(false); }
  }
  return <Modal title={type === "ENTRY" ? "Registrar entrada" : "Registrar saída"} onClose={onClose}><form className="modal-form" onSubmit={submit}><div className={`stock-movement-hero ${type === "ENTRY" ? "incoming" : "outgoing"}`}><span>{type === "ENTRY" ? <ArrowDownToLine /> : <ArrowUpFromLine />}</span><div><strong>{type === "ENTRY" ? "Adicionar ao estoque" : "Retirar do estoque"}</strong><small>O saldo e o histórico serão atualizados imediatamente.</small></div></div><label>Produto<select name="productId" required defaultValue=""><option value="" disabled>Selecione um produto</option>{products.map((product) => <option key={product.id} value={product.id}>{product.name} · saldo {quantity(product.stockQuantity)}</option>)}</select></label><div className="two-columns"><label>Quantidade<input name="quantity" type="number" min="0.001" step="0.001" required /></label>{type === "ENTRY" && <label>Custo unitário<input name="unitCost" inputMode="decimal" placeholder="0,00" /></label>}</div><label>Motivo<input name="reason" required placeholder={type === "ENTRY" ? "Ex.: Compra de fornecedor" : "Ex.: Perda, consumo ou transferência"} /></label><label>Documento ou referência<input name="reference" placeholder="Opcional" /></label>{error && <div className="form-error">{error}</div>}<div className="modal-actions"><button type="button" className="button button-ghost" onClick={onClose}>Cancelar</button><button className="button button-primary" disabled={saving}>{saving ? "Salvando..." : "Confirmar movimentação"}</button></div></form></Modal>;
}

type XmlChoice = { value: string; salePrice: string };

function XmlImportModal({ products, onClose, onImported }: { products: Product[]; onClose: () => void; onImported: () => Promise<void> }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");
  const [xml, setXml] = useState("");
  const [document, setDocument] = useState<XmlDocument | null>(null);
  const [choices, setChoices] = useState<Record<number, XmlChoice>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ importedProducts: number; createdProducts: number; totalQuantity: number } | null>(null);
  const selectedCount = useMemo(() => Object.values(choices).filter((choice) => choice.value !== "IGNORE").length, [choices]);

  async function selectFile(file: File | undefined) {
    if (!file) return;
    setLoading(true); setError(""); setDocument(null); setResult(null);
    try {
      if (!file.name.toLowerCase().endsWith(".xml")) throw new Error("Selecione um arquivo com extensão .xml.");
      const content = await file.text();
      const response = await api<{ document: XmlDocument }>("/api/stock/xml/preview", { method: "POST", body: JSON.stringify({ xml: content }) });
      setFileName(file.name); setXml(content); setDocument(response.document);
      setChoices(Object.fromEntries(response.document.items.map((item) => [item.index, {
        value: item.suggestedProductId ? `LINK:${item.suggestedProductId}` : "CREATE",
        salePrice: (item.unitCostCents / 100).toFixed(2),
      }])));
    } catch (requestError) { setError((requestError as Error).message); }
    finally { setLoading(false); }
  }

  async function importXml() {
    if (!document || !xml) return;
    setLoading(true); setError("");
    try {
      const mappings = document.items.map((item) => {
        const choice = choices[item.index] ?? { value: "IGNORE", salePrice: "0" };
        if (choice.value === "CREATE") return { index: item.index, action: "CREATE", salePriceCents: toCents(choice.salePrice) };
        if (choice.value.startsWith("LINK:")) return { index: item.index, action: "LINK", productId: choice.value.slice(5) };
        return { index: item.index, action: "IGNORE" };
      });
      const imported = await api<{ importedProducts: number; createdProducts: number; totalQuantity: number }>("/api/stock/xml/import", { method: "POST", body: JSON.stringify({ xml, mappings }) });
      setResult(imported);
      await onImported();
    } catch (requestError) { setError((requestError as Error).message); }
    finally { setLoading(false); }
  }

  if (result) return <Modal title="Nota fiscal importada" onClose={onClose}><div className="xml-success"><span><CheckCircle2 /></span><h3>Entrada concluída!</h3><p>O estoque e o custo médio dos produtos foram atualizados.</p><div><strong>{result.importedProducts}</strong><small>itens vinculados</small><strong>{result.createdProducts}</strong><small>produtos criados</small><strong>{quantity(result.totalQuantity)}</strong><small>unidades recebidas</small></div><button className="button button-primary button-full" onClick={onClose}>Concluir</button></div></Modal>;

  return <Modal title="Importar XML da nota fiscal" onClose={onClose} wide><div className="xml-import">
    {!document ? <div className="xml-upload"><button type="button" onClick={() => inputRef.current?.click()}><FileUp /><strong>{loading ? "Analisando o XML..." : "Selecionar XML da NF-e ou NFC-e"}</strong><small>O arquivo será analisado antes de qualquer alteração no estoque.</small></button><input ref={inputRef} type="file" accept=".xml,text/xml,application/xml" hidden onChange={(event) => void selectFile(event.target.files?.[0])} />{fileName && <p>{fileName}</p>}{error && <div className="form-error">{error}</div>}</div> : <>
      <div className="xml-document-summary"><span><FileText /></span><div><small>NOTA FISCAL</small><strong>NF {document.documentNumber || "Sem número"} · Série {document.series || "—"}</strong><p>{document.supplierName || "Fornecedor não informado"}{document.supplierTaxId ? ` · ${document.supplierTaxId}` : ""}</p></div><div><small>TOTAL DA NOTA</small><strong>{formatMoney(document.totalCents)}</strong><p>{document.items.length} itens no XML</p></div></div>
      {document.alreadyImported && <div className="xml-warning"><TriangleAlert /><div><strong>Esta nota já foi importada</strong><p>Para evitar estoque duplicado, uma mesma chave de acesso não pode ser confirmada novamente.</p></div></div>}
      <div className="xml-items"><div className="xml-items-head"><div><strong>Vincule os itens da nota</strong><small>{selectedCount} de {document.items.length} itens entrarão no estoque</small></div><button type="button" className="button button-ghost button-small" onClick={() => { setDocument(null); setXml(""); setFileName(""); }}>Trocar arquivo</button></div>{document.items.map((item) => { const choice = choices[item.index] ?? { value: "IGNORE", salePrice: "0" }; return <article key={item.index}><div className="xml-item-info"><span>{item.index + 1}</span><div><strong>{item.description}</strong><small>Cód. {item.code || "—"} · EAN {item.barcode || "—"} · NCM {item.ncm || "—"}</small></div><div><strong>{quantity(item.quantity)} {item.unit || "un."}</strong><small>{formatMoney(item.unitCostCents)} por unidade</small></div></div><div className="xml-item-mapping"><label>Destino no estoque<select value={choice.value} onChange={(event) => setChoices((current) => ({ ...current, [item.index]: { ...choice, value: event.target.value } }))}><option value="IGNORE">Ignorar este item</option><option value="CREATE">Cadastrar como novo produto</option>{products.map((product) => <option key={product.id} value={`LINK:${product.id}`}>{product.name} · saldo {quantity(product.stockQuantity)}</option>)}</select></label>{choice.value === "CREATE" && <label>Preço de venda<input value={choice.salePrice} inputMode="decimal" onChange={(event) => setChoices((current) => ({ ...current, [item.index]: { ...choice, salePrice: event.target.value } }))} /></label>}</div></article>; })}</div>
      {error && <div className="form-error">{error}</div>}
      <div className="xml-footer"><div><strong>{selectedCount} itens selecionados</strong><small>Confira os vínculos antes de confirmar.</small></div><div><button type="button" className="button button-ghost" onClick={onClose}>Cancelar</button><button type="button" className="button button-primary" disabled={loading || document.alreadyImported || selectedCount === 0} onClick={() => void importXml()}>{loading ? "Importando..." : "Confirmar entrada no estoque"}</button></div></div>
    </>}
  </div></Modal>;
}
